<template>
  <ion-page>
    <ion-header :translucent="true">
      <ion-toolbar>
      </ion-toolbar>
      <ion-toolbar class="breadcrumb-bar" v-if="activeSection">
        <div class="breadcrumb">
          <span class="crumb-link" @click="activeSection = null">{{ t("Account") }}</span>
          <span class="crumb-sep">›</span>
          <span class="crumb-current">{{ activeSection.label }}</span>
        </div>
      </ion-toolbar>
    </ion-header>

    <ion-content :fullscreen="true">
      <Transition name="slide-up" mode="out-in">

        <!-- Top-level tile grid -->
        <div v-if="!activeSection" class="tile-grid" key="tiles">

          <div class="tile tile-account" @click="openSection(sections.account)">
            <ion-icon :icon="personCircleOutline" class="tile-icon" aria-hidden="true" />
            <span class="tile-label">{{ t("Account") }}</span>
            <span class="tile-sub">{{ t("Sign in, subscribe & prefs") }}</span>
          </div>

          <div class="tile tile-videos" @click="openSection(sections.videos)">
            <ion-icon :icon="videocamOutline" class="tile-icon" aria-hidden="true" />
            <span class="tile-label">{{ t("Videos") }}</span>
            <span class="tile-sub">{{ t("Tutorials & feature guides") }}</span>
          </div>

          <div class="tile tile-bigbuttons" @click="openSection(sections.bigButtons)">
            <ion-icon :icon="eyeOutline" class="tile-icon" aria-hidden="true" />
            <span class="tile-label">{{ t("Big Buttons & EyeGaze") }}</span>
            <span class="tile-sub">{{ t("Accessibility settings") }}</span>
          </div>

          <div class="tile tile-support" @click="openSection(sections.support)">
            <ion-icon :icon="helpCircleOutline" class="tile-icon" aria-hidden="true" />
            <span class="tile-label">{{ t("Support") }}</span>
            <span class="tile-sub">{{ t("Help, contact & what's new") }}</span>
          </div>

        </div>

        <!-- Drill-down list -->
        <div v-else class="drill-down" key="list">
          <ion-list :inset="true">

            <ion-item-group v-for="group in activeSection.groups" :key="group.id">
              <ion-item-divider v-if="group.label">
                <ion-label>{{ group.label }}</ion-label>
              </ion-item-divider>

              <ion-item
                v-for="item in group.items"
                :key="item.id"
                button
                :detail="true"
                @click="navigateTo(item)"
              >

                <div class="item-icon-wrap" slot="start" :class="item.iconClass">
                  <ion-icon :icon="item.icon" class="item-icon" aria-hidden="true" />
                </div>
                <ion-label>
                  <h2>{{ item.label }}</h2>
                  <p v-if="item.description">{{ item.description }}</p>
                </ion-label>
              </ion-item>

            </ion-item-group>

            <!-- Version number at bottom of Support -->
            <div v-if="activeSection.id === 'support'" class="version-block">
              <p class="version-text">ChessVis V{{ versionID }}</p>
              <p class="version-text">© 2026, Conceptual Software, Inc.</p>
            </div>

          </ion-list>
        </div>

      </Transition>
    </ion-content>

    <BottomNav @bottom-click="onClick"/>
  </ion-page>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import {
  IonPage, IonHeader, IonToolbar, IonTitle,
  IonButtons, IonBackButton, IonContent,
  IonIcon, IonList, IonItem, IonItemGroup,
  IonItemDivider, IonLabel, modalController
} from '@ionic/vue'
import {
  personCircleOutline, videocamOutline, eyeOutline,
  helpCircleOutline, logInOutline, personAddOutline,
  cardOutline, settingsOutline, trashOutline,
  playCircleOutline, accessibilityOutline,
  informationCircleOutline, chatbubbleOutline, downloadOutline,
  documentTextOutline, sparklesOutline, timeOutline, checkmarkCircleOutline, mailOutline
} from 'ionicons/icons'
import BottomNav from '@/components/BottomNav.vue'
import { useConnectionStore } from '@/stores/connectionStore';
import { t } from '@/composables/translateText.js'
import { computed } from "@vue/reactivity"
import ModalIOSInstall from '@/components/ModalIOSInstall.vue'

import { usePwaStore } from '@/stores/PwaStore'
import { storeToRefs } from 'pinia';


const emit = defineEmits(['menu-item-click'])

const pwaStore = usePwaStore()
const connectionStore = useConnectionStore()
const isSignedIn = connectionStore.getDummy == 0
const versionID = ref(connectionStore.versionID)

  // ── Computed ───────────────────────────────────────────────────────────────
  const isIos = computed(() =>
    /iphone|ipad|ipod/.test(navigator.userAgent.toLowerCase())
  )

  const isInstalled = computed(() =>
    window.matchMedia('(display-mode: standalone)').matches
  )

  const showIOSTile = computed(() =>
    (isIos.value && !isInstalled.value && !isDismissed.value)
  )

interface MenuItem {
  id: string
  label: string
  description?: string
  icon: string
  iconClass?: string
  route: string,
}

interface MenuGroup {
  id: string
  label?: string
  items: MenuItem[]
}

interface Section {
  id: string
  label: string
  groups: MenuGroup[]
}

const signinElement =  {
            id: 'signin',
            label: t('Sign In'),
            description: t('Sign in to your ChessVis account'),
            icon: logInOutline,
            iconClass: 'icon-gray',
            route: 'signin',
          }
const registerElement =  {
            id: 'register',
            label: t('Register'),
            description: t('Create a new ChessVis account'),
            icon: personAddOutline,
            iconClass: 'icon-gray',
            route: 'register',
          }
const signoffElement = {
            id: 'signout',
            label: t('Sign Off'),
            description: t('Sign off of your ChessVis account'),
            icon: logInOutline,
            iconClass: 'icon-gray',
            route: 'signoff',
          }
const verifyEmailElement = {
            id: 'verify',
            label: t('Verify Email Address'),
            description: t('Verified address needed to subscribe'),
            icon: mailOutline,
            iconClass: 'icon-gray',
            route: 'verify_email',
          }
const verifiedEmailElement = {
            id: 'verified',
            label: t('Email Address Verified'),
            description: t('Thanks for verifying your email address'),
            icon: checkmarkCircleOutline,
            iconClass: 'icon-gray',
            route: '',
          }
var loginElements = []
if (!isSignedIn) {
  loginElements.push(signinElement)
  loginElements.push(registerElement)
}
else {
  loginElements.push(signoffElement)
  if (connectionStore.getEmailVerified != "y" && connectionStore.getEmailVerified != "yes")
    loginElements.push(verifyEmailElement)
  else
    loginElements.push(verifiedEmailElement)
}

const subscribeLabel = (connectionStore.getIsSubscriber == 1 && connectionStore.getSubscriberExpireDisplayString != "") ?
                t("Subscriber") + ": " + connectionStore.getSubscriberExpireDisplayString :
                t("Subscribe")
const subscribeDescription = (connectionStore.getIsSubscriber == 1 && connectionStore.getSubscriberExpireDisplayString != "") ?
                t("Thank you for subscribing and supporting my work") :
                t("Unlock Chessvis Studio, Blindfold and support my work")

var originalSupportItems = [
          {
            id: 'pwa',
            label: t('Install APP'),
            description: t('Add ChessVis to your home screen'),
            icon: downloadOutline,
            iconClass: 'icon-gray',
            route: 'pwa',
          },
          {
            id: 'welcome',
            label: t('Welcome & App Landing'),
            description: t('Introduction and overview of ChessVis'),
            icon: informationCircleOutline,
            iconClass: 'icon-gray',
            route: 'welcome',
          },
          {
            id: 'whats-new',
            label: t("What's New"),
            description: t('Latest updates and improvements'),
            icon: sparklesOutline,
            iconClass: 'icon-gray',
            route: 'whats_new',
          },
          {
            id: 'contact',
            label: t('Contact'),
            description: t('Get in touch with support'),
            icon: chatbubbleOutline,
            iconClass: 'icon-gray',
            route: 'contact',
          },
          {
            id: 'terms',
            label: t('Terms of Service'),
            description: t('ChessVis terms and privacy policy'),
            icon: documentTextOutline,
            iconClass: 'icon-gray',
            route: 'tos',
          },
        ]

const { isInstallable } = storeToRefs(pwaStore);
const supportItems = (isInstallable.value) ? originalSupportItems : originalSupportItems.slice(1)

const sections = {

  account: {
    id: 'account',
    label: t('Account'),
    groups: [
      {
        id: 'auth',
        label: t('Access'),
        items: loginElements,
      },
      {
        id: 'subscription',
        label: t('Subscription'),
        items: [
          {
            id: 'subscribe',
            label: subscribeLabel,
            description: subscribeDescription,
            icon: cardOutline,
            iconClass: 'icon-purple',
            route: 'subscribe_blurb',
          },
        ],
      },
      {
        id: 'prefs',
        label: t('Preferences'),
        items: [
          {
            id: 'preferences',
            label: t('Preferences & Languages'),
            description: t('App settings, board style and language'),
            icon: settingsOutline,
            iconClass: 'icon-gray',
            route: 'preferences',
          },
        ],
      },
      {
        id: 'danger',
        label: t('Leaving'),
        items: [
          {
            id: 'delete',
            label: t('Delete Account'),
            description: t('Permanently delete your account and data'),
            icon: trashOutline,
            iconClass: 'icon-red',
            route: 'delete_account',
          },
          {
            id: 'cancel',
            label: t('Cancel Subscription'),
            description: t('Cancel renewal of your subscription'),
            icon: trashOutline,
            iconClass: 'icon-red',
            route: 'cancel_subscription',
          },
        ],
      },
    ],
  },

  videos: {
    id: 'videos',
    label: t('Videos'),
    groups: [
      {
        id: 'openings',
        label: t('Opening Mastery Videos'),
        items: [
          {
            id: 'opening-opponent',
            label: t('Opening — Find Opponent Moves'),
            description: t('How to find the moves your opponents play'),
            icon: playCircleOutline,
            iconClass: 'icon-purple',
            route: 'video_opponent_moves',
          },
          {
            id: 'opening-best',
            label: t('Opening — Find Your Best Moves'),
            description: t('How to find your strongest responses'),
            icon: playCircleOutline,
            iconClass: 'icon-purple',
            route: 'video_best_moves',
          },
          {
            id: 'repertoires-video',
            label: t('Repertoires!'),
            description: t('Building and drilling your repertoire'),
            icon: sparklesOutline,
            iconClass: 'icon-purple',
            route: 'video_repertoires',
          },
        ],
      },
      {
        id: 'tactics-videos',
        label: t('Tactics Gym Videos'),
        items: [
          {
            id: 'puzzle-viz-video',
            label: t('Puzzle Visualization'),
            description: t('How Puzzle Visualization works'),
            icon: playCircleOutline,
            iconClass: 'icon-teal',
            route: 'video_puzzle_visualization',
          },
          {
            id: 'guarding-video',
            label: t("Who's Guarding Whom"),
            description: t('Introduction to the guarding exercise'),
            icon: playCircleOutline,
            iconClass: 'icon-teal',
            route: 'video_guarding',
          },
          {
            id: 'whoo-video',
            label: 'Whoo',
            description: t('Just - Whoo'),
            icon: playCircleOutline,
            iconClass: 'icon-teal',
            route: 'video_whoo',
          },
        ],
      },
      {
        id: 'legacy-videos',
        label: t('Legacy Videos'),
        items: [
          {
            id: 'legacy-rep-video',
            label: t('Legacy — Repertoires'),
            description: t('Classic repertoire tools walkthrough'),
            icon: timeOutline,
            iconClass: 'icon-legacy',
            route: 'video_legacy_repertoires',
          },
          {
            id: 'legacy-master-video',
            label: t('Legacy — Master Player Games'),
            description: t('Using the master player explorer'),
            icon: timeOutline,
            iconClass: 'icon-legacy',
            route: 'video_legacy_master',
          },
          {
            id: 'legacy-collection-video',
            label: t('Legacy — Play Game Collection'),
            description: t('Playing against game collections'),
            icon: timeOutline,
            iconClass: 'icon-legacy',
            route: 'video_legacy_play_collection',
          },
        ],
      },
    ],
  },

  bigButtons: {
    id: 'big-buttons',
    label: t('Big Buttons & EyeGaze'),
    groups: [
      {
        id: 'bigbutton-items',
        items: [
          {
            id: 'bigbutton-menu',
            label: t('Big Button Menu'),
            description: t('Launch the large button navigation'),
            icon: accessibilityOutline,
            iconClass: 'icon-gray',
            route: 'big_button_menu',
          },
          {
            id: 'bigbutton-prefs',
            label: t('Preferences'),
            description: t('Configure big button and eye gaze settings'),
            icon: settingsOutline,
            iconClass: 'icon-gray',
            route: 'big_button_preferences',
          },
        ],
      },
    ],
  },

  support: {
    id: 'support',
    label: t('Support'),
    groups: [
      {
        id: 'support-items',
        items: supportItems
      },
    ],
  },

}

const activeSection = ref<Section | null>(null)

const sectionInUse = connectionStore.getMenuSectionInUse
//console.log("section in use in studio",sectionInUse)
if (sectionInUse != null && sectionInUse != "") {
  switch (sectionInUse) {
    case "account" :
      activeSection.value = sections.account
      break
    case "videos" :
      activeSection.value = sections.videos
      break
    case "big-buttons" :
      activeSection.value = sections.bigButtons
      break
    case "support" :
      activeSection.value = sections.support
      break
  }
}
console.log("active section",activeSection.value)

 const openIOSModal = async () => {
    const modal = await modalController.create({
      component: ModalIOSInstall
    });

    modal.present();

    const { data, role } = await modal.onWillDismiss();
  };

function openSection(section: Section) {
  activeSection.value = section
}

function navigateTo(item: MenuItem) {
    //console.log("account item",item)

    if (item.route == "pwa") {
         if (showIOSTile.value)
        openIOSModal()
      else
        pwaStore.installApp()
      return
    }
    // set the return values for clicking on the done button
    connectionStore.setMenuInUse("account")
    connectionStore.setMenuSectionInUse(activeSection.value.id)

    onClick(item.route,item.label,"FROM_NEW_MENU")
  }

  function onClick(path : string,label : string,whichMenu : string) {
    //console.log("Landing page CLICK ",path,label)
    if (path != "") {
      connectionStore.setIsUsingNewMenus(true)
      emit("menu-item-click",path,label,whichMenu)
    }
  }
</script>

<style scoped>
ion-toolbar {
  --background: #12122a;
  --color: #ffffff;
  --border-color: #2a2a4a;
}

ion-back-button { --color: #AFA9EC; }

ion-title {
  font-size: 17px;
  font-weight: 500;
}

.breadcrumb-bar {
  --background: #0f0f22;
  --min-height: 32px;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 10px;
  font-size: 16px;
}

.crumb-link    { color: #AFA9EC; cursor: pointer; }
.crumb-sep     { color: #444466; }
.crumb-current { color: #888880; }

ion-content { --background: #1a1a2e; }

/* ── Tile grid ── */
.tile-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-template-rows: repeat(2, minmax(0, 1fr));
  gap: 12px;
  padding: 16px;
  height: 100%;
  box-sizing: border-box;
}

.tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 18px;
  padding: 20px 16px;
  cursor: pointer;
  transition: opacity 0.15s ease, transform 0.1s ease;
  -webkit-tap-highlight-color: transparent;
}

.tile:active {
  opacity: 0.85;
  transform: scale(0.97);
}

.tile-account   { background: #2a2a3a; }
.tile-videos    { background: #1a2a3a; }
.tile-bigbuttons { background: #1a2a2a; }
.tile-support   { background: #2a1a2a; }

.tile-account .tile-icon,
.tile-account .tile-label   { color: #D3D1C7; }
.tile-account .tile-sub     { color: #888780; }

.tile-videos .tile-icon,
.tile-videos .tile-label    { color: #7FB5D3; }
.tile-videos .tile-sub      { color: #4A8AAA; }

.tile-bigbuttons .tile-icon,
.tile-bigbuttons .tile-label { color: #7FD3C7; }
.tile-bigbuttons .tile-sub   { color: #4AAAA0; }

.tile-support .tile-icon,
.tile-support .tile-label   { color: #C7A0D3; }
.tile-support .tile-sub     { color: #9A70AA; }

.tile-icon {
  font-size: 32px;
}

.tile-label {
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  line-height: 1.3;
}

.tile-sub {
  font-size: 11px;
  text-align: center;
  line-height: 1.3;
  opacity: 0.85;
}

/* ── Drill-down list ── */
.drill-down { padding-top: 8px; }

ion-list { --background: transparent; background: transparent; }

ion-item-divider {
  --background: #0f0f22;
  --color: #AFA9EC;
  --padding-start: 16px;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  min-height: 32px;
}

ion-item {
  --background: #1e1e35;
  --background-activated: #2a2a45;
  --color: #e0e0f0;
  --border-color: #2a2a4a;
  --detail-icon-color: #AFA9EC;
  --detail-icon-opacity: 1;
  --padding-start: 12px;
  --inner-padding-end: 12px;
  --min-height: 62px;
}

ion-item h2 {
  font-size: 15px;
  font-weight: 400;
  color: #e0e0f0;
  margin-bottom: 3px;
}

ion-item p {
  font-size: 12px;
  color: #b0b0bc;
  line-height: 1.4;
  white-space: normal;
}

.item-icon-wrap {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 4px;
  flex-shrink: 0;
}

.item-icon { font-size: 19px; }

.icon-gray    { background: #2a2a3a; }
.icon-gray .item-icon { color: #D3D1C7; }

.icon-purple  { background: #3C3489; }
.icon-purple .item-icon { color: #CECBF6; }

.icon-teal    { background: #085041; }
.icon-teal .item-icon { color: #9FE1CB; }

.icon-legacy  { background: #1e1e2e; }
.icon-legacy .item-icon { color: #666680; }

.icon-red     { background: #3a1a1a; }
.icon-red .item-icon { color: #e07070; }

/* ── Version block ── */
.version-block {
  padding: 20px 16px 8px;
  text-align: center;
}

.version-text {
  font-size: 12px;
  color: #aaaacc;
  margin: 2px 0;
}

/* ── Transition ── */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}
.slide-up-enter-from { opacity: 0; transform: translateY(12px); }
.slide-up-leave-to   { opacity: 0; transform: translateY(-8px); }
</style>
